// "Apagar histórico" (pedido 2026-09-08, aba Perfil do PWA) — cliente
// escolhe veículo + período e confirma com a própria senha. Nunca apaga
// nada de verdade no Traccar: só marca o período como oculto (ver
// gpsdrive-history-deletions.service.ts) — recuperável por um
// Administrador da empresa (ver vehicle-logs-panel.tsx).
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { subDays, startOfDay, endOfDay } from "date-fns";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import type { Vehicle } from "@/types";
import { useCreateHistoryDeletion } from "@/hooks/queries";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type PeriodKey = "hoje" | "ontem" | "24h" | "3d" | "7d" | "all";

const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: "hoje", label: "Hoje" },
  { key: "ontem", label: "Ontem" },
  { key: "24h", label: "Últimas 24 horas" },
  { key: "3d", label: "Últimos 3 dias" },
  { key: "7d", label: "Últimos 7 dias" },
  { key: "all", label: "Todo o histórico" },
];

// Mesmo espírito de periodRange() em route-history-center.tsx, sem a
// variante "custom" (esta tela só oferece presets — pedido do usuário:
// "todo o período, 1 dia atrás, últimas 24h etc").
function periodRange(period: PeriodKey): { from: Date; to: Date } {
  const now = new Date();
  switch (period) {
    case "hoje":
      return { from: startOfDay(now), to: now };
    case "ontem":
      return { from: startOfDay(subDays(now, 1)), to: endOfDay(subDays(now, 1)) };
    case "24h":
      return { from: new Date(now.getTime() - 24 * 60 * 60 * 1000), to: now };
    case "3d":
      return { from: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000), to: now };
    case "7d":
      return { from: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000), to: now };
    case "all":
      // Não existe "desde quando o veículo existe" consultável aqui —
      // epoch cobre qualquer posição que o Traccar já tenha guardado.
      return { from: new Date(0), to: now };
  }
}

export function VehicleHistoryDeleteDialog({
  vehicles,
  open,
  onOpenChange,
}: {
  vehicles: Vehicle[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [vehicleId, setVehicleId] = useState<string>("");
  const [period, setPeriod] = useState<PeriodKey>("24h");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateHistoryDeletion();

  useEffect(() => {
    if (!open) return;
    setVehicleId((prev) => (prev && vehicles.some((v) => v.id === prev) ? prev : vehicles[0]?.id ?? ""));
    setPeriod("24h");
    setPassword("");
    setError(null);
  }, [open, vehicles]);

  const vehicle = useMemo(() => vehicles.find((v) => v.id === vehicleId) ?? null, [vehicles, vehicleId]);
  const periodLabel = PERIODS.find((p) => p.key === period)?.label ?? period;

  const handleSubmit = async () => {
    if (!vehicle) return;
    if (!password) {
      setError("Digite sua senha para confirmar.");
      return;
    }
    setError(null);
    const { from, to } = periodRange(period);
    try {
      await createMutation.mutateAsync({
        traccarDeviceId: Number(vehicle.id),
        periodFrom: from.toISOString(),
        periodTo: to.toISOString(),
        periodLabel,
        password,
      });
      toast.success(`Histórico de ${vehicle.name} apagado (${periodLabel}).`);
      onOpenChange(false);
    } catch (err) {
      if (err instanceof GpsDriveApiError && err.status === 401) {
        setError("Senha incorreta.");
      } else {
        setError("Não foi possível apagar o histórico agora. Tente de novo em alguns instantes.");
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-rose-600" />
            Apagar histórico
          </DialogTitle>
          <DialogDescription>
            O período escolhido some das suas consultas de histórico. A empresa consegue recuperar
            depois, se você precisar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {vehicles.length > 1 && (
            <div className="space-y-1.5">
              <Label>Veículo</Label>
              <Select value={vehicleId} onValueChange={setVehicleId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um veículo" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name} — {v.plate}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Período</Label>
            <Select value={period} onValueChange={(v) => setPeriod(v as PeriodKey)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIODS.map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="history-delete-password">Confirme sua senha</Label>
            <Input
              id="history-delete-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="Sua senha de login"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={createMutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={!vehicle || createMutation.isPending}
          >
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Apagar histórico"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
